import React, { useEffect, useState } from "react";
import {
  getParties,
  createParty,
  updateParty,
  deleteParty,
} from "../api";

// Format the date received from the backend
const formatCreatedAt = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

export default function PartyView() {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);

  const [form, setForm] = useState({
    name: "",
    contact: "",
    address: "",
    debit: 0,
    credit: 0,
    paymentStatus: "Pending",
  });

  // Load all parties
  const loadParties = async () => {
    setLoading(true);

    try {
      const data = await getParties();
      setParties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load parties:", err);
      alert("Failed to load parties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParties();
  }, []);

  // Open Add Party modal
  const openAddModal = () => {
    setEditingParty(null);

    setForm({
      name: "",
      contact: "",
      address: "",
      debit: 0,
      credit: 0,
      paymentStatus: "Pending",
    });

    setShowModal(true);
  };

  // Open Edit Party modal
  const openEditModal = (party) => {
    setEditingParty(party);

    setForm({
      name: party.name || "",
      contact: party.contact || "",
      address: party.address || "",
      debit: party.debit ?? 0,
      credit: party.credit ?? 0,
      paymentStatus: party.paymentStatus || "Pending",
    });

    setShowModal(true);
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setForm((previous) => ({
      ...previous,
      [name]:
        name === "debit" || name === "credit"
          ? Number(value || 0)
          : value,
    }));
  };

  // Add or update party
  const handleSave = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      debit: Number(form.debit || 0),
      credit: Number(form.credit || 0),
    };

    try {
      if (editingParty) {
        await updateParty(editingParty.id, payload);
      } else {
        await createParty(payload);
      }

      setShowModal(false);
      setEditingParty(null);

      await loadParties();
    } catch (err) {
      console.error("Failed to save party:", err);
      alert("Failed to save party");
    }
  };

  // Delete party
  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Delete this party (and linked expenses)?"
    );

    if (!confirmed) return;

    try {
      await deleteParty(id);
      await loadParties();
    } catch (err) {
      console.error("Failed to delete party:", err);
      alert("Failed to delete party");
    }
  };

  // Update payment status
  const handleStatusChange = async (party, paymentStatus) => {
    try {
      await updateParty(party.id, {
        name: party.name || "",
        contact: party.contact || "",
        address: party.address || "",
        debit: Number(party.debit || 0),
        credit: Number(party.credit || 0),
        paymentStatus,
      });

      await loadParties();
    } catch (err) {
      console.error("Failed to update payment status:", err);
      alert("Failed to update status");
    }
  };

  return (
    <div className="so-parties p-4 md:p-8 h-full overflow-auto">

      {/* Scoped SwiftOrder theme */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-parties {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;

          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          background-image: radial-gradient(
            circle at 1px 1px,
            var(--line) 1px,
            transparent 1.6px
          );
          background-size: 24px 24px;

          font-family: var(--font-body);
          color: var(--ink);

          /* Layout reacts to the width of this panel, not the whole screen,
             so it also works next to the sidebar. */
          container-type: inline-size;
          -webkit-overflow-scrolling: touch;
        }

        .so-parties * {
          box-sizing: border-box;
        }

        .so-parties-title {
          font-family: var(--font-display);
          font-size: 1.9rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-parties-add-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-parties-add-btn:hover {
          background: var(--chili-dark);
        }

        .so-parties-table-wrap {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
          overflow-x: auto;
        }

        .so-parties-table {
          width: 100%;
          min-width: 1000px;
          border-collapse: collapse;
        }

        .so-parties-thead {
          background: var(--paper-dim);
          font-family: var(--font-mono);
          font-size: 0.7rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--stone);
        }

        .so-parties-thead th {
          white-space: nowrap;
        }

        .so-parties-row {
          border-top: 1px solid var(--line);
          transition: background 0.1s ease;
        }

        .so-parties-row:hover {
          background: var(--paper-dim);
        }

        .so-parties-debit {
          font-weight: 700;
          color: var(--chili-dark);
        }

        .so-parties-credit {
          font-weight: 700;
          color: var(--herb-dark);
        }

        .so-parties-select {
          border: 1px solid var(--line);
          border-radius: 6px;
          background: var(--paper);
          color: var(--ink);
          outline: none;
          font-size: 0.78rem;
        }

        .so-parties-select:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-parties-edit-btn {
          background: var(--paper-dim);
          color: var(--ink);
          border-radius: 6px;
          transition: background 0.15s ease;
        }

        .so-parties-edit-btn:hover {
          background: var(--line);
        }

        .so-parties-delete-btn {
          background: #fdece6;
          color: var(--chili-dark);
          border-radius: 6px;
          transition: background 0.15s ease;
        }

        .so-parties-delete-btn:hover {
          background: #f09595;
        }

        .so-parties-empty {
          color: var(--stone-light);
        }

        .so-parties-modal-overlay {
          background: rgba(28, 26, 23, 0.55);
        }

        .so-parties-modal-card {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 24px 50px -20px rgba(28, 26, 23, 0.45);
          max-height: 90vh;
          overflow-y: auto;
        }

        .so-parties-modal-header {
          border-bottom: 1px solid var(--line);
        }

        .so-parties-modal-title {
          font-family: var(--font-display);
          font-size: 1.5rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-parties-modal-close {
          color: var(--stone);
          transition: color 0.15s ease;
        }

        .so-parties-modal-close:hover {
          color: var(--chili);
        }

        .so-parties-label {
          font-size: 0.78rem;
          font-weight: 700;
          font-family: var(--font-mono);
          letter-spacing: 0.03em;
          color: var(--stone);
          display: block;
          margin-bottom: 6px;
        }

        .so-parties-input {
          width: 100%;
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 9px 12px;
          background: var(--paper);
          color: var(--ink);
          outline: none;
          font-size: 0.93rem;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-parties-input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-parties-cancel-btn {
          border: 1px solid var(--line);
          color: var(--ink);
          background: #fffdf8;
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-parties-cancel-btn:hover {
          background: var(--paper-dim);
        }

        .so-parties-save-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-parties-save-btn:hover {
          background: var(--chili-dark);
        }

        .so-parties-created-date {
          white-space: nowrap;
          font-size: 0.75rem;
          color: var(--stone);
        }

        @media (max-width: 640px) {
          .so-parties-modal-overlay {
            align-items: flex-end;
            padding: 0;
          }

          .so-parties-modal-card {
            max-width: none;
            max-height: 92vh;
            border-radius: 16px 16px 0 0;
            border-bottom: 0;
          }

          .so-parties-input {
            font-size: 16px; /* stops iOS from zooming into the field */
            min-height: 46px;
          }

          .so-parties-modal-close {
            min-width: 40px;
            min-height: 40px;
            font-size: 1.1rem;
          }

          .so-parties-modal-actions {
            flex-direction: column-reverse;
          }

          .so-parties-modal-actions button {
            width: 100%;
            min-height: 46px;
            font-weight: 700;
          }
        }

        @media (max-width: 400px) {
          .so-parties-money-grid {
            grid-template-columns: 1fr;
          }
        }

        /* ---------- Responsive ---------- */

        /* Panel narrower than 1000px: each party becomes a card */
        @container (max-width: 1000px) {
          .so-parties .so-parties-table-wrap {
            background: transparent;
            border: 0;
            box-shadow: none;
            overflow: visible;
          }

          .so-parties .so-parties-table,
          .so-parties .so-parties-table tbody {
            display: block;
            min-width: 0;
            width: 100%;
          }

          .so-parties .so-parties-thead {
            display: none;
          }

          .so-parties .so-parties-row {
            display: block;
            background: #fffdf8;
            border: 1px solid var(--line);
            border-radius: 12px;
            box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
            margin-bottom: 14px;
            padding: 6px 16px 14px;
            overflow: hidden;
          }

          .so-parties .so-parties-row:hover {
            background: #fffdf8;
          }

          .so-parties .so-parties-row td {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            padding: 10px 0;
            text-align: right;
            border-top: 1px dashed var(--line);
            overflow-wrap: anywhere;
          }

          .so-parties .so-parties-row td::before {
            content: attr(data-label);
            flex: none;
            font-family: var(--font-mono);
            font-size: 0.68rem;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            font-weight: 500;
            color: var(--stone);
            text-align: left;
          }

          .so-parties .so-parties-row td.so-parties-name-cell {
            display: block;
            border-top: 0;
            padding: 12px 0 8px;
            text-align: left;
            font-size: 1.05rem;
            font-weight: 800;
          }

          .so-parties .so-parties-row td.so-parties-name-cell::before {
            display: none;
          }

          .so-parties .so-parties-row td.so-parties-actions-cell {
            display: block;
            padding: 14px 0 0;
          }

          .so-parties .so-parties-row td.so-parties-actions-cell::before {
            display: none;
          }

          .so-parties .so-parties-actions-cell > div {
            display: flex;
            gap: 10px;
          }

          .so-parties .so-parties-edit-btn,
          .so-parties .so-parties-delete-btn {
            flex: 1;
            min-height: 44px;
            font-size: 0.85rem;
            font-weight: 700;
          }

          .so-parties .so-parties-select {
            min-height: 40px;
            font-size: 16px;
            padding: 6px 10px;
          }

          .so-parties .so-parties-state-row {
            display: block;
            background: #fffdf8;
            border: 1px solid var(--line);
            border-radius: 12px;
            padding: 18px;
          }

          .so-parties .so-parties-state-row td {
            display: block;
            border-top: 0;
            padding: 0;
            text-align: center;
          }

          .so-parties .so-parties-state-row td::before {
            display: none;
          }
        }

        /* Panel narrower than 640px: phone */
        @container (max-width: 640px) {
          .so-parties .so-parties-title {
            font-size: 1.5rem;
            line-height: 1.15;
          }

          .so-parties .so-parties-header {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .so-parties .so-parties-add-btn {
            width: 100%;
            min-height: 46px;
            font-weight: 700;
          }
        }
      `}</style>

      {/* Page Header */}
      <div className="so-parties-header flex justify-between items-center mb-6">
        <h2 className="so-parties-title">
          Parties / Customers / Suppliers
        </h2>

        <button
          type="button"
          onClick={openAddModal}
          className="so-parties-add-btn px-4 py-2"
        >
          + Add Party
        </button>
      </div>

      {/* Parties Table */}
      <div className="so-parties-table-wrap">
        <table className="so-parties-table text-sm">
          <thead className="so-parties-thead">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Contact</th>
              <th className="px-4 py-3 text-left">Address</th>
              <th className="px-4 py-3 text-right">
                Debit (They owe us)
              </th>
              <th className="px-4 py-3 text-right">
                Credit (We owe them)
              </th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-left">Created Date</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr className="so-parties-state-row">
                <td
                  colSpan={8}
                  className="so-parties-empty px-4 py-4 text-center"
                >
                  Loading parties...
                </td>
              </tr>
            )}

            {!loading && parties.length === 0 && (
              <tr className="so-parties-state-row">
                <td
                  colSpan={8}
                  className="so-parties-empty px-4 py-4 text-center"
                >
                  No parties yet.
                </td>
              </tr>
            )}

            {!loading &&
              parties.map((p) => (
                <tr key={p.id} className="so-parties-row">
                  <td className="so-parties-name-cell px-4 py-3 font-semibold">
                    {p.name || "—"}
                  </td>

                  <td className="px-4 py-3" data-label="Contact">
                    {p.contact || "—"}
                  </td>

                  <td className="px-4 py-3" data-label="Address">
                    {p.address || "—"}
                  </td>

                  <td
                    className="px-4 py-3 text-right so-parties-debit"
                    data-label="Debit (They owe us)"
                  >
                    ₹{Number(p.debit || 0).toFixed(2)}
                  </td>

                  <td
                    className="px-4 py-3 text-right so-parties-credit"
                    data-label="Credit (We owe them)"
                  >
                    ₹{Number(p.credit || 0).toFixed(2)}
                  </td>

                  <td className="px-4 py-3 text-center" data-label="Status">
                    <select
                      value={p.paymentStatus || "Pending"}
                      onChange={(e) =>
                        handleStatusChange(p, e.target.value)
                      }
                      className="so-parties-select px-2 py-1"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid ✅</option>
                    </select>
                  </td>

                  {/* Created Date */}
                  <td
                    className="px-4 py-3 so-parties-created-date"
                    data-label="Created"
                  >
                    {formatCreatedAt(p.createdAt)}
                  </td>

                  <td className="so-parties-actions-cell px-4 py-3 text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(p)}
                        className="so-parties-edit-btn px-2 py-1 text-xs"
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        className="so-parties-delete-btn px-2 py-1 text-xs"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Party Modal */}
      {showModal && (
        <div className="so-parties-modal-overlay fixed inset-0 flex items-center justify-center z-50 px-4">

          <div className="so-parties-modal-card w-full max-w-lg">

            {/* Modal Header */}
            <div className="so-parties-modal-header flex justify-between items-center px-6 py-4">
              <h3 className="so-parties-modal-title">
                {editingParty ? "Edit Party" : "Add Party"}
              </h3>

              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setEditingParty(null);
                }}
                className="so-parties-modal-close"
              >
                ✕
              </button>
            </div>

            {/* Modal Form */}
            <form
              onSubmit={handleSave}
              className="px-6 py-4 space-y-4"
            >
              {/* Name */}
              <div>
                <label className="so-parties-label">
                  Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  className="so-parties-input"
                  placeholder="Enter party name"
                  required
                />
              </div>

              {/* Contact */}
              <div>
                <label className="so-parties-label">
                  Contact
                </label>

                <input
                  type="text"
                  name="contact"
                  value={form.contact}
                  onChange={handleFormChange}
                  className="so-parties-input"
                  placeholder="Enter contact number"
                />
              </div>

              {/* Address */}
              <div>
                <label className="so-parties-label">
                  Address
                </label>

                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleFormChange}
                  className="so-parties-input"
                  placeholder="Enter address"
                  rows={3}
                />
              </div>

              {/* Debit and Credit */}
              <div className="so-parties-money-grid grid grid-cols-2 gap-3">

                <div>
                  <label className="so-parties-label">
                    Debit (They owe us)
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="debit"
                    value={form.debit}
                    onChange={handleFormChange}
                    className="so-parties-input"
                  />
                </div>

                <div>
                  <label className="so-parties-label">
                    Credit (We owe them)
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="credit"
                    value={form.credit}
                    onChange={handleFormChange}
                    className="so-parties-input"
                  />
                </div>

              </div>

              {/* Payment Status */}
              <div>
                <label className="so-parties-label">
                  Payment Status
                </label>

                <select
                  name="paymentStatus"
                  value={form.paymentStatus}
                  onChange={handleFormChange}
                  className="so-parties-input"
                >
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid ✅</option>
                </select>
              </div>

              {/* Buttons */}
              <div className="so-parties-modal-actions flex justify-end gap-3 pt-2">

                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingParty(null);
                  }}
                  className="so-parties-cancel-btn px-4 py-2"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="so-parties-save-btn px-4 py-2"
                >
                  {editingParty ? "Update Party" : "Add Party"}
                </button>

              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}





// import React, { useEffect, useState } from "react";
// import {
//   getParties,
//   createParty,
//   updateParty,
//   deleteParty,
// } from "../api";

// // Format the date received from the backend
// const formatCreatedAt = (value) => {
//   if (!value) return "—";

//   const date = new Date(value);

//   if (Number.isNaN(date.getTime())) {
//     return "—";
//   }

//   return new Intl.DateTimeFormat("en-IN", {
//     day: "2-digit",
//     month: "short",
//     year: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: true,
//   }).format(date);
// };

// export default function PartyView() {
//   const [parties, setParties] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [showModal, setShowModal] = useState(false);
//   const [editingParty, setEditingParty] = useState(null);

//   const [form, setForm] = useState({
//     name: "",
//     contact: "",
//     address: "",
//     debit: 0,
//     credit: 0,
//     paymentStatus: "Pending",
//   });

//   // Load all parties
//   const loadParties = async () => {
//     setLoading(true);

//     try {
//       const data = await getParties();
//       setParties(Array.isArray(data) ? data : []);
//     } catch (err) {
//       console.error("Failed to load parties:", err);
//       alert("Failed to load parties");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadParties();
//   }, []);

//   // Open Add Party modal
//   const openAddModal = () => {
//     setEditingParty(null);

//     setForm({
//       name: "",
//       contact: "",
//       address: "",
//       debit: 0,
//       credit: 0,
//       paymentStatus: "Pending",
//     });

//     setShowModal(true);
//   };

//   // Open Edit Party modal
//   const openEditModal = (party) => {
//     setEditingParty(party);

//     setForm({
//       name: party.name || "",
//       contact: party.contact || "",
//       address: party.address || "",
//       debit: party.debit ?? 0,
//       credit: party.credit ?? 0,
//       paymentStatus: party.paymentStatus || "Pending",
//     });

//     setShowModal(true);
//   };

//   // Handle form input changes
//   const handleFormChange = (e) => {
//     const { name, value } = e.target;

//     setForm((previous) => ({
//       ...previous,
//       [name]:
//         name === "debit" || name === "credit"
//           ? Number(value || 0)
//           : value,
//     }));
//   };

//   // Add or update party
//   const handleSave = async (e) => {
//     e.preventDefault();

//     const payload = {
//       ...form,
//       debit: Number(form.debit || 0),
//       credit: Number(form.credit || 0),
//     };

//     try {
//       if (editingParty) {
//         await updateParty(editingParty.id, payload);
//       } else {
//         await createParty(payload);
//       }

//       setShowModal(false);
//       setEditingParty(null);

//       await loadParties();
//     } catch (err) {
//       console.error("Failed to save party:", err);
//       alert("Failed to save party");
//     }
//   };

//   // Delete party
//   const handleDelete = async (id) => {
//     const confirmed = window.confirm(
//       "Delete this party (and linked expenses)?"
//     );

//     if (!confirmed) return;

//     try {
//       await deleteParty(id);
//       await loadParties();
//     } catch (err) {
//       console.error("Failed to delete party:", err);
//       alert("Failed to delete party");
//     }
//   };

//   // Update payment status
//   const handleStatusChange = async (party, paymentStatus) => {
//     try {
//       await updateParty(party.id, {
//         name: party.name || "",
//         contact: party.contact || "",
//         address: party.address || "",
//         debit: Number(party.debit || 0),
//         credit: Number(party.credit || 0),
//         paymentStatus,
//       });

//       await loadParties();
//     } catch (err) {
//       console.error("Failed to update payment status:", err);
//       alert("Failed to update status");
//     }
//   };

//   return (
//     <div className="so-parties p-4 md:p-8 h-full overflow-auto">

//       {/* Scoped SwiftOrder theme */}
//       <style>{`
//         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

//         .so-parties {
//           --ink: #1c1a17;
//           --paper: #fbf9f4;
//           --paper-dim: #f2ede1;
//           --chili: #e4572e;
//           --chili-dark: #c43f1a;
//           --herb: #3f6b4f;
//           --herb-dark: #2d4f39;
//           --stone: #6b6459;
//           --stone-light: #a49c8d;
//           --line: #ddd4c2;

//           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
//           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
//           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

//           background: var(--paper-dim);
//           background-image: radial-gradient(
//             circle at 1px 1px,
//             var(--line) 1px,
//             transparent 1.6px
//           );
//           background-size: 24px 24px;

//           font-family: var(--font-body);
//           color: var(--ink);
//         }

//         .so-parties * {
//           box-sizing: border-box;
//         }

//         .so-parties-title {
//           font-family: var(--font-display);
//           font-size: 1.9rem;
//           letter-spacing: 0.01em;
//           color: var(--ink);
//         }

//         .so-parties-add-btn {
//           background: var(--chili);
//           color: var(--paper);
//           border-radius: 8px;
//           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
//           transition: background 0.15s ease;
//         }

//         .so-parties-add-btn:hover {
//           background: var(--chili-dark);
//         }

//         .so-parties-table-wrap {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//           box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
//           overflow-x: auto;
//         }

//         .so-parties-table {
//           width: 100%;
//           min-width: 1000px;
//           border-collapse: collapse;
//         }

//         .so-parties-thead {
//           background: var(--paper-dim);
//           font-family: var(--font-mono);
//           font-size: 0.7rem;
//           letter-spacing: 0.06em;
//           text-transform: uppercase;
//           color: var(--stone);
//         }

//         .so-parties-thead th {
//           white-space: nowrap;
//         }

//         .so-parties-row {
//           border-top: 1px solid var(--line);
//           transition: background 0.1s ease;
//         }

//         .so-parties-row:hover {
//           background: var(--paper-dim);
//         }

//         .so-parties-debit {
//           font-weight: 700;
//           color: var(--chili-dark);
//         }

//         .so-parties-credit {
//           font-weight: 700;
//           color: var(--herb-dark);
//         }

//         .so-parties-select {
//           border: 1px solid var(--line);
//           border-radius: 6px;
//           background: var(--paper);
//           color: var(--ink);
//           outline: none;
//           font-size: 0.78rem;
//         }

//         .so-parties-select:focus {
//           border-color: var(--chili);
//           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
//         }

//         .so-parties-edit-btn {
//           background: var(--paper-dim);
//           color: var(--ink);
//           border-radius: 6px;
//           transition: background 0.15s ease;
//         }

//         .so-parties-edit-btn:hover {
//           background: var(--line);
//         }

//         .so-parties-delete-btn {
//           background: #fdece6;
//           color: var(--chili-dark);
//           border-radius: 6px;
//           transition: background 0.15s ease;
//         }

//         .so-parties-delete-btn:hover {
//           background: #f09595;
//         }

//         .so-parties-empty {
//           color: var(--stone-light);
//         }

//         .so-parties-modal-overlay {
//           background: rgba(28, 26, 23, 0.55);
//         }

//         .so-parties-modal-card {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//           box-shadow: 0 24px 50px -20px rgba(28, 26, 23, 0.45);
//           max-height: 90vh;
//           overflow-y: auto;
//         }

//         .so-parties-modal-header {
//           border-bottom: 1px solid var(--line);
//         }

//         .so-parties-modal-title {
//           font-family: var(--font-display);
//           font-size: 1.5rem;
//           letter-spacing: 0.01em;
//           color: var(--ink);
//         }

//         .so-parties-modal-close {
//           color: var(--stone);
//           transition: color 0.15s ease;
//         }

//         .so-parties-modal-close:hover {
//           color: var(--chili);
//         }

//         .so-parties-label {
//           font-size: 0.78rem;
//           font-weight: 700;
//           font-family: var(--font-mono);
//           letter-spacing: 0.03em;
//           color: var(--stone);
//           display: block;
//           margin-bottom: 6px;
//         }

//         .so-parties-input {
//           width: 100%;
//           border: 1px solid var(--line);
//           border-radius: 8px;
//           padding: 9px 12px;
//           background: var(--paper);
//           color: var(--ink);
//           outline: none;
//           font-size: 0.93rem;
//           transition: border-color 0.15s ease, box-shadow 0.15s ease;
//         }

//         .so-parties-input:focus {
//           border-color: var(--chili);
//           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
//         }

//         .so-parties-cancel-btn {
//           border: 1px solid var(--line);
//           color: var(--ink);
//           background: #fffdf8;
//           border-radius: 8px;
//           transition: background 0.15s ease;
//         }

//         .so-parties-cancel-btn:hover {
//           background: var(--paper-dim);
//         }

//         .so-parties-save-btn {
//           background: var(--chili);
//           color: var(--paper);
//           border-radius: 8px;
//           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
//           transition: background 0.15s ease;
//         }

//         .so-parties-save-btn:hover {
//           background: var(--chili-dark);
//         }

//         .so-parties-created-date {
//           white-space: nowrap;
//           font-size: 0.75rem;
//           color: var(--stone);
//         }

//         @media (max-width: 640px) {
//           .so-parties-title {
//             font-size: 1.5rem;
//           }

//           .so-parties-header {
//             align-items: flex-start;
//             gap: 12px;
//             flex-direction: column;
//           }

//           .so-parties-add-btn {
//             width: 100%;
//           }
//         }
//       `}</style>

//       {/* Page Header */}
//       <div className="so-parties-header flex justify-between items-center mb-6">
//         <h2 className="so-parties-title">
//           Parties / Customers / Suppliers
//         </h2>

//         <button
//           type="button"
//           onClick={openAddModal}
//           className="so-parties-add-btn px-4 py-2"
//         >
//           + Add Party
//         </button>
//       </div>

//       {/* Parties Table */}
//       <div className="so-parties-table-wrap">
//         <table className="so-parties-table text-sm">
//           <thead className="so-parties-thead">
//             <tr>
//               <th className="px-4 py-3 text-left">Name</th>
//               <th className="px-4 py-3 text-left">Contact</th>
//               <th className="px-4 py-3 text-left">Address</th>
//               <th className="px-4 py-3 text-right">
//                 Debit (They owe us)
//               </th>
//               <th className="px-4 py-3 text-right">
//                 Credit (We owe them)
//               </th>
//               <th className="px-4 py-3 text-center">Status</th>
//               <th className="px-4 py-3 text-left">Created Date</th>
//               <th className="px-4 py-3 text-center">Actions</th>
//             </tr>
//           </thead>

//           <tbody>
//             {loading && (
//               <tr>
//                 <td
//                   colSpan={8}
//                   className="so-parties-empty px-4 py-4 text-center"
//                 >
//                   Loading parties...
//                 </td>
//               </tr>
//             )}

//             {!loading && parties.length === 0 && (
//               <tr>
//                 <td
//                   colSpan={8}
//                   className="so-parties-empty px-4 py-4 text-center"
//                 >
//                   No parties yet.
//                 </td>
//               </tr>
//             )}

//             {!loading &&
//               parties.map((p) => (
//                 <tr key={p.id} className="so-parties-row">
//                   <td className="px-4 py-3 font-semibold">
//                     {p.name || "—"}
//                   </td>

//                   <td className="px-4 py-3">
//                     {p.contact || "—"}
//                   </td>

//                   <td className="px-4 py-3">
//                     {p.address || "—"}
//                   </td>

//                   <td className="px-4 py-3 text-right so-parties-debit">
//                     ₹{Number(p.debit || 0).toFixed(2)}
//                   </td>

//                   <td className="px-4 py-3 text-right so-parties-credit">
//                     ₹{Number(p.credit || 0).toFixed(2)}
//                   </td>

//                   <td className="px-4 py-3 text-center">
//                     <select
//                       value={p.paymentStatus || "Pending"}
//                       onChange={(e) =>
//                         handleStatusChange(p, e.target.value)
//                       }
//                       className="so-parties-select px-2 py-1"
//                     >
//                       <option value="Pending">Pending</option>
//                       <option value="Paid">Paid ✅</option>
//                     </select>
//                   </td>

//                   {/* Created Date */}
//                   <td className="px-4 py-3 so-parties-created-date">
//                     {formatCreatedAt(p.createdAt)}
//                   </td>

//                   <td className="px-4 py-3 text-center">
//                     <div className="flex justify-center gap-2">
//                       <button
//                         type="button"
//                         onClick={() => openEditModal(p)}
//                         className="so-parties-edit-btn px-2 py-1 text-xs"
//                       >
//                         Edit
//                       </button>

//                       <button
//                         type="button"
//                         onClick={() => handleDelete(p.id)}
//                         className="so-parties-delete-btn px-2 py-1 text-xs"
//                       >
//                         Delete
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Add / Edit Party Modal */}
//       {showModal && (
//         <div className="so-parties-modal-overlay fixed inset-0 flex items-center justify-center z-50 px-4">

//           <div className="so-parties-modal-card w-full max-w-lg">

//             {/* Modal Header */}
//             <div className="so-parties-modal-header flex justify-between items-center px-6 py-4">
//               <h3 className="so-parties-modal-title">
//                 {editingParty ? "Edit Party" : "Add Party"}
//               </h3>

//               <button
//                 type="button"
//                 onClick={() => {
//                   setShowModal(false);
//                   setEditingParty(null);
//                 }}
//                 className="so-parties-modal-close"
//               >
//                 ✕
//               </button>
//             </div>

//             {/* Modal Form */}
//             <form
//               onSubmit={handleSave}
//               className="px-6 py-4 space-y-4"
//             >
//               {/* Name */}
//               <div>
//                 <label className="so-parties-label">
//                   Name
//                 </label>

//                 <input
//                   type="text"
//                   name="name"
//                   value={form.name}
//                   onChange={handleFormChange}
//                   className="so-parties-input"
//                   placeholder="Enter party name"
//                   required
//                 />
//               </div>

//               {/* Contact */}
//               <div>
//                 <label className="so-parties-label">
//                   Contact
//                 </label>

//                 <input
//                   type="text"
//                   name="contact"
//                   value={form.contact}
//                   onChange={handleFormChange}
//                   className="so-parties-input"
//                   placeholder="Enter contact number"
//                 />
//               </div>

//               {/* Address */}
//               <div>
//                 <label className="so-parties-label">
//                   Address
//                 </label>

//                 <textarea
//                   name="address"
//                   value={form.address}
//                   onChange={handleFormChange}
//                   className="so-parties-input"
//                   placeholder="Enter address"
//                   rows={3}
//                 />
//               </div>

//               {/* Debit and Credit */}
//               <div className="grid grid-cols-2 gap-3">

//                 <div>
//                   <label className="so-parties-label">
//                     Debit (They owe us)
//                   </label>

//                   <input
//                     type="number"
//                     min="0"
//                     step="0.01"
//                     name="debit"
//                     value={form.debit}
//                     onChange={handleFormChange}
//                     className="so-parties-input"
//                   />
//                 </div>

//                 <div>
//                   <label className="so-parties-label">
//                     Credit (We owe them)
//                   </label>

//                   <input
//                     type="number"
//                     min="0"
//                     step="0.01"
//                     name="credit"
//                     value={form.credit}
//                     onChange={handleFormChange}
//                     className="so-parties-input"
//                   />
//                 </div>

//               </div>

//               {/* Payment Status */}
//               <div>
//                 <label className="so-parties-label">
//                   Payment Status
//                 </label>

//                 <select
//                   name="paymentStatus"
//                   value={form.paymentStatus}
//                   onChange={handleFormChange}
//                   className="so-parties-input"
//                 >
//                   <option value="Pending">Pending</option>
//                   <option value="Paid">Paid ✅</option>
//                 </select>
//               </div>

//               {/* Buttons */}
//               <div className="flex justify-end gap-3 pt-2">

//                 <button
//                   type="button"
//                   onClick={() => {
//                     setShowModal(false);
//                     setEditingParty(null);
//                   }}
//                   className="so-parties-cancel-btn px-4 py-2"
//                 >
//                   Cancel
//                 </button>

//                 <button
//                   type="submit"
//                   className="so-parties-save-btn px-4 py-2"
//                 >
//                   {editingParty ? "Update Party" : "Add Party"}
//                 </button>

//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }










// // import React, { useEffect, useState } from "react";
// // import {
// //   getParties,
// //   createParty,
// //   updateParty,
// //   deleteParty,
// // } from "../api";

// // export default function PartyView() {
// //   const [parties, setParties] = useState([]);
// //   const [loading, setLoading] = useState(false);

// //   const [showModal, setShowModal] = useState(false);
// //   const [editingParty, setEditingParty] = useState(null);

// //   const [form, setForm] = useState({
// //     name: "",
// //     contact: "",
// //     address: "",
// //     debit: 0,
// //     credit: 0,
// //     paymentStatus: "Pending",
// //   });

// //   const loadParties = async () => {
// //     setLoading(true);
// //     try {
// //       const data = await getParties();
// //       setParties(data || []);
// //     } catch (err) {
// //       console.error("Failed to load parties", err);
// //       alert("Failed to load parties");
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   useEffect(() => {
// //     loadParties();
// //   }, []);

// //   const openAddModal = () => {
// //     setEditingParty(null);
// //     setForm({
// //       name: "",
// //       contact: "",
// //       address: "",
// //       debit: 0,
// //       credit: 0,
// //       paymentStatus: "Pending",
// //     });
// //     setShowModal(true);
// //   };

// //   const openEditModal = (party) => {
// //     setEditingParty(party);
// //     setForm({
// //       name: party.name || "",
// //       contact: party.contact || "",
// //       address: party.address || "",
// //       debit: party.debit ?? 0,
// //       credit: party.credit ?? 0,
// //       paymentStatus: party.paymentStatus || "Pending",
// //     });
// //     setShowModal(true);
// //   };

// //   const handleFormChange = (e) => {
// //     const { name, value } = e.target;
// //     setForm((f) => ({
// //       ...f,
// //       [name]:
// //         name === "debit" || name === "credit"
// //           ? Number(value || 0)
// //           : value,
// //     }));
// //   };

// //   const handleSave = async (e) => {
// //     e.preventDefault();
// //     const payload = { ...form };

// //     try {
// //       if (editingParty) {
// //         await updateParty(editingParty.id, payload);
// //       } else {
// //         await createParty(payload);
// //       }
// //       setShowModal(false);
// //       await loadParties();
// //     } catch (err) {
// //       console.error("Failed to save party", err);
// //       alert("Failed to save party");
// //     }
// //   };

// //   const handleDelete = async (id) => {
// //     if (!window.confirm("Delete this party (and linked expenses)?")) return;
// //     try {
// //       await deleteParty(id);
// //       await loadParties();
// //     } catch (err) {
// //       console.error("Failed to delete party", err);
// //       alert("Failed to delete party");
// //     }
// //   };

// //   const handleStatusChange = async (party, paymentStatus) => {
// //     try {
// //       await updateParty(party.id, {
// //         ...party,
// //         paymentStatus,
// //       });
// //       await loadParties();
// //     } catch (err) {
// //       console.error("Failed to update payment status", err);
// //       alert("Failed to update status");
// //     }
// //   };

// //   return (
// //     <div className="so-parties p-4 md:p-8 h-full overflow-auto">

// //       {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
// //       <style>{`
// //         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// //         .so-parties {
// //           --ink: #1c1a17;
// //           --paper: #fbf9f4;
// //           --paper-dim: #f2ede1;
// //           --chili: #e4572e;
// //           --chili-dark: #c43f1a;
// //           --herb: #3f6b4f;
// //           --herb-dark: #2d4f39;
// //           --stone: #6b6459;
// //           --stone-light: #a49c8d;
// //           --line: #ddd4c2;
// //           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
// //           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
// //           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

// //           background: var(--paper-dim);
// //           background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
// //           background-size: 24px 24px;
// //           font-family: var(--font-body);
// //           color: var(--ink);
// //         }

// //         .so-parties * {
// //           box-sizing: border-box;
// //         }

// //         .so-parties-title {
// //           font-family: var(--font-display);
// //           font-size: 1.9rem;
// //           letter-spacing: 0.01em;
// //           color: var(--ink);
// //         }

// //         .so-parties-add-btn {
// //           background: var(--chili);
// //           color: var(--paper);
// //           border-radius: 8px;
// //           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
// //           transition: background 0.15s ease;
// //         }

// //         .so-parties-add-btn:hover {
// //           background: var(--chili-dark);
// //         }

// //         .so-parties-table-wrap {
// //           background: #fffdf8;
// //           border: 1px solid var(--line);
// //           border-radius: 12px;
// //           box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
// //           overflow: hidden;
// //         }

// //         .so-parties-thead {
// //           background: var(--paper-dim);
// //           font-family: var(--font-mono);
// //           font-size: 0.7rem;
// //           letter-spacing: 0.06em;
// //           text-transform: uppercase;
// //           color: var(--stone);
// //         }

// //         .so-parties-row {
// //           border-top: 1px solid var(--line);
// //           transition: background 0.1s ease;
// //         }

// //         .so-parties-row:hover {
// //           background: var(--paper-dim);
// //         }

// //         .so-parties-debit {
// //           font-weight: 700;
// //           color: var(--chili-dark);
// //         }

// //         .so-parties-credit {
// //           font-weight: 700;
// //           color: var(--herb-dark);
// //         }

// //         .so-parties-select {
// //           border: 1px solid var(--line);
// //           border-radius: 6px;
// //           background: var(--paper);
// //           color: var(--ink);
// //           outline: none;
// //           font-size: 0.78rem;
// //           transition: border-color 0.15s ease, box-shadow 0.15s ease;
// //         }

// //         .so-parties-select:focus {
// //           border-color: var(--chili);
// //           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
// //         }

// //         .so-parties-edit-btn {
// //           background: var(--paper-dim);
// //           color: var(--ink);
// //           border-radius: 6px;
// //           transition: background 0.15s ease;
// //         }

// //         .so-parties-edit-btn:hover {
// //           background: var(--line);
// //         }

// //         .so-parties-delete-btn {
// //           background: #fdece6;
// //           color: var(--chili-dark);
// //           border-radius: 6px;
// //           transition: background 0.15s ease;
// //         }

// //         .so-parties-delete-btn:hover {
// //           background: #f09595;
// //         }

// //         .so-parties-empty {
// //           color: var(--stone-light);
// //         }

// //         .so-parties-modal-overlay {
// //           background: rgba(28, 26, 23, 0.55);
// //         }

// //         .so-parties-modal-card {
// //           background: #fffdf8;
// //           border: 1px solid var(--line);
// //           border-radius: 12px;
// //           box-shadow: 0 24px 50px -20px rgba(28, 26, 23, 0.45);
// //         }

// //         .so-parties-modal-header {
// //           border-bottom: 1px solid var(--line);
// //         }

// //         .so-parties-modal-title {
// //           font-family: var(--font-display);
// //           font-size: 1.5rem;
// //           letter-spacing: 0.01em;
// //           color: var(--ink);
// //         }

// //         .so-parties-modal-close {
// //           color: var(--stone);
// //           transition: color 0.15s ease;
// //         }

// //         .so-parties-modal-close:hover {
// //           color: var(--chili);
// //         }

// //         .so-parties-label {
// //           font-size: 0.78rem;
// //           font-weight: 700;
// //           font-family: var(--font-mono);
// //           letter-spacing: 0.03em;
// //           color: var(--stone);
// //           display: block;
// //           margin-bottom: 6px;
// //         }

// //         .so-parties-input {
// //           width: 100%;
// //           border: 1px solid var(--line);
// //           border-radius: 8px;
// //           padding: 9px 12px;
// //           background: var(--paper);
// //           color: var(--ink);
// //           outline: none;
// //           font-size: 0.93rem;
// //           transition: border-color 0.15s ease, box-shadow 0.15s ease;
// //         }

// //         .so-parties-input:focus {
// //           border-color: var(--chili);
// //           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
// //         }

// //         .so-parties-cancel-btn {
// //           border: 1px solid var(--line);
// //           color: var(--ink);
// //           background: #fffdf8;
// //           border-radius: 8px;
// //           transition: background 0.15s ease;
// //         }

// //         .so-parties-cancel-btn:hover {
// //           background: var(--paper-dim);
// //         }

// //         .so-parties-save-btn {
// //           background: var(--chili);
// //           color: var(--paper);
// //           border-radius: 8px;
// //           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
// //           transition: background 0.15s ease;
// //         }

// //         .so-parties-save-btn:hover {
// //           background: var(--chili-dark);
// //         }
// //       `}</style>

// //       <div className="flex justify-between items-center mb-6">
// //         <h2 className="so-parties-title">
// //           Parties / Customers / Suppliers
// //         </h2>
// //         <button
// //           onClick={openAddModal}
// //           className="so-parties-add-btn px-4 py-2"
// //         >
// //           Add Party
// //         </button>
// //       </div>

// //       <div className="so-parties-table-wrap">
// //         <table className="min-w-full text-sm">
// //           <thead className="so-parties-thead">
// //             <tr>
// //               <th className="px-4 py-3 text-left">Name</th>
// //               <th className="px-4 py-3 text-left">Contact</th>
// //               <th className="px-4 py-3 text-left">Address</th>
// //               <th className="px-4 py-3 text-right">Debit (They owe us)</th>
// //               <th className="px-4 py-3 text-right">Credit (We owe them)</th>
// //               <th className="px-4 py-3 text-center">Status</th>
// //               <th className="px-4 py-3 text-center">Actions</th>
// //             </tr>
// //           </thead>
// //           <tbody>
// //             {loading && (
// //               <tr>
// //                 <td
// //                   colSpan={7}
// //                   className="so-parties-empty px-4 py-4 text-center"
// //                 >
// //                   Loading...
// //                 </td>
// //               </tr>
// //             )}
// //             {!loading && parties.length === 0 && (
// //               <tr>
// //                 <td
// //                   colSpan={7}
// //                   className="so-parties-empty px-4 py-4 text-center"
// //                 >
// //                   No parties yet.
// //                 </td>
// //               </tr>
// //             )}
// //             {!loading &&
// //               parties.map((p) => (
// //                 <tr key={p.id} className="so-parties-row">
// //                   <td className="px-4 py-3">{p.name}</td>
// //                   <td className="px-4 py-3">{p.contact}</td>
// //                   <td className="px-4 py-3">{p.address}</td>
// //                   <td className="px-4 py-3 text-right so-parties-debit">
// //                     ₹{(p.debit ?? 0).toFixed(2)}
// //                   </td>
// //                   <td className="px-4 py-3 text-right so-parties-credit">
// //                     ₹{(p.credit ?? 0).toFixed(2)}
// //                   </td>
// //                   <td className="px-4 py-3 text-center">
// //                     <select
// //                       value={p.paymentStatus || "Pending"}
// //                       onChange={(e) =>
// //                         handleStatusChange(p, e.target.value)
// //                       }
// //                       className="so-parties-select px-2 py-1"
// //                     >
// //                       <option value="Pending">Pending</option>
// //                       <option value="Paid">Paid ✅</option>
// //                     </select>
// //                   </td>
// //                   <td className="px-4 py-3 text-center">
// //                     <div className="flex justify-center gap-2">
// //                       <button
// //                         onClick={() => openEditModal(p)}
// //                         className="so-parties-edit-btn px-2 py-1 text-xs"
// //                       >
// //                         Edit
// //                       </button>
// //                       <button
// //                         onClick={() => handleDelete(p.id)}
// //                         className="so-parties-delete-btn px-2 py-1 text-xs"
// //                       >
// //                         Delete
// //                       </button>
// //                     </div>
// //                   </td>
// //                 </tr>
// //               ))}
// //           </tbody>
// //         </table>
// //       </div>

// //       {/* Modal */}
// //       {showModal && (
// //         <div className="so-parties-modal-overlay fixed inset-0 flex items-center justify-center z-50 px-4">
// //           <div className="so-parties-modal-card w-full max-w-lg">
// //             <div className="so-parties-modal-header flex justify-between items-center px-6 py-4">
// //               <h3 className="so-parties-modal-title">
// //                 {editingParty ? "Edit Party" : "Add Party"}
// //               </h3>
// //               <button
// //                 onClick={() => setShowModal(false)}
// //                 className="so-parties-modal-close"
// //               >
// //                 ✕
// //               </button>
// //             </div>

// //             <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
// //               <div>
// //                 <label className="so-parties-label">Name</label>
// //                 <input
// //                   name="name"
// //                   value={form.name}
// //                   onChange={handleFormChange}
// //                   className="so-parties-input"
// //                   required
// //                 />
// //               </div>

// //               <div>
// //                 <label className="so-parties-label">Contact</label>
// //                 <input
// //                   name="contact"
// //                   value={form.contact}
// //                   onChange={handleFormChange}
// //                   className="so-parties-input"
// //                 />
// //               </div>

// //               <div>
// //                 <label className="so-parties-label">Address</label>
// //                 <textarea
// //                   name="address"
// //                   value={form.address}
// //                   onChange={handleFormChange}
// //                   className="so-parties-input"
// //                 />
// //               </div>

// //               <div className="grid grid-cols-2 gap-3">
// //                 <div>
// //                   <label className="so-parties-label">
// //                     Debit (They owe us)
// //                   </label>
// //                   <input
// //                     type="number"
// //                     step="0.01"
// //                     name="debit"
// //                     value={form.debit}
// //                     onChange={handleFormChange}
// //                     className="so-parties-input"
// //                   />
// //                 </div>
// //                 <div>
// //                   <label className="so-parties-label">
// //                     Credit (We owe them)
// //                   </label>
// //                   <input
// //                     type="number"
// //                     step="0.01"
// //                     name="credit"
// //                     value={form.credit}
// //                     onChange={handleFormChange}
// //                     className="so-parties-input"
// //                   />
// //                 </div>
// //               </div>

// //               <div>
// //                 <label className="so-parties-label">
// //                   Payment Status
// //                 </label>
// //                 <select
// //                   name="paymentStatus"
// //                   value={form.paymentStatus}
// //                   onChange={handleFormChange}
// //                   className="so-parties-input"
// //                 >
// //                   <option value="Pending">Pending</option>
// //                   <option value="Paid">Paid ✅</option>
// //                 </select>
// //               </div>

// //               <div className="flex justify-end gap-3 pt-2">
// //                 <button
// //                   type="button"
// //                   onClick={() => setShowModal(false)}
// //                   className="so-parties-cancel-btn px-4 py-2"
// //                 >
// //                   Cancel
// //                 </button>
// //                 <button
// //                   type="submit"
// //                   className="so-parties-save-btn px-4 py-2"
// //                 >
// //                   {editingParty ? "Update" : "Add"}
// //                 </button>
// //               </div>
// //             </form>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }





// // import React, { useEffect, useState } from "react";
// // import {
// //   getParties,
// //   createParty,
// //   updateParty,
// //   deleteParty,
// // } from "../api";

// // export default function PartyView() {
// //   const [parties, setParties] = useState([]);
// //   const [loading, setLoading] = useState(false);

// //   const [showModal, setShowModal] = useState(false);
// //   const [editingParty, setEditingParty] = useState(null);

// //   const [form, setForm] = useState({
// //     name: "",
// //     contact: "",
// //     address: "",
// //     debit: 0,
// //     credit: 0,
// //     paymentStatus: "Pending",
// //   });

// //   const loadParties = async () => {
// //     setLoading(true);
// //     try {
// //       const data = await getParties();
// //       setParties(data || []);
// //     } catch (err) {
// //       console.error("Failed to load parties", err);
// //       alert("Failed to load parties");
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   useEffect(() => {
// //     loadParties();
// //   }, []);

// //   const openAddModal = () => {
// //     setEditingParty(null);
// //     setForm({
// //       name: "",
// //       contact: "",
// //       address: "",
// //       debit: 0,
// //       credit: 0,
// //       paymentStatus: "Pending",
// //     });
// //     setShowModal(true);
// //   };

// //   const openEditModal = (party) => {
// //     setEditingParty(party);
// //     setForm({
// //       name: party.name || "",
// //       contact: party.contact || "",
// //       address: party.address || "",
// //       debit: party.debit ?? 0,
// //       credit: party.credit ?? 0,
// //       paymentStatus: party.paymentStatus || "Pending",
// //     });
// //     setShowModal(true);
// //   };

// //   const handleFormChange = (e) => {
// //     const { name, value } = e.target;
// //     setForm((f) => ({
// //       ...f,
// //       [name]:
// //         name === "debit" || name === "credit"
// //           ? Number(value || 0)
// //           : value,
// //     }));
// //   };

// //   const handleSave = async (e) => {
// //     e.preventDefault();
// //     const payload = { ...form };

// //     try {
// //       if (editingParty) {
// //         await updateParty(editingParty.id, payload);
// //       } else {
// //         await createParty(payload);
// //       }
// //       setShowModal(false);
// //       await loadParties();
// //     } catch (err) {
// //       console.error("Failed to save party", err);
// //       alert("Failed to save party");
// //     }
// //   };

// //   const handleDelete = async (id) => {
// //     if (!window.confirm("Delete this party (and linked expenses)?")) return;
// //     try {
// //       await deleteParty(id);
// //       await loadParties();
// //     } catch (err) {
// //       console.error("Failed to delete party", err);
// //       alert("Failed to delete party");
// //     }
// //   };

// //   const handleStatusChange = async (party, paymentStatus) => {
// //     try {
// //       await updateParty(party.id, {
// //         ...party,
// //         paymentStatus,
// //       });
// //       await loadParties();
// //     } catch (err) {
// //       console.error("Failed to update payment status", err);
// //       alert("Failed to update status");
// //     }
// //   };

// //   return (
// //     <div className="p-8 h-full overflow-auto">
// //       <div className="flex justify-between items-center mb-6">
// //         <h2 className="text-3xl font-bold text-slate-800">
// //           Parties / Customers / Suppliers
// //         </h2>
// //         <button
// //           onClick={openAddModal}
// //           className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
// //         >
// //           Add Party
// //         </button>
// //       </div>

// //       <div className="bg-white rounded-lg shadow overflow-hidden">
// //         <table className="min-w-full text-sm">
// //           <thead className="bg-slate-100">
// //             <tr>
// //               <th className="px-4 py-2 text-left">Name</th>
// //               <th className="px-4 py-2 text-left">Contact</th>
// //               <th className="px-4 py-2 text-left">Address</th>
// //               <th className="px-4 py-2 text-right">Debit (They owe us)</th>
// //               <th className="px-4 py-2 text-right">Credit (We owe them)</th>
// //               <th className="px-4 py-2 text-center">Status</th>
// //               <th className="px-4 py-2 text-center">Actions</th>
// //             </tr>
// //           </thead>
// //           <tbody>
// //             {loading && (
// //               <tr>
// //                 <td
// //                   colSpan={7}
// //                   className="px-4 py-4 text-center text-slate-500"
// //                 >
// //                   Loading...
// //                 </td>
// //               </tr>
// //             )}
// //             {!loading && parties.length === 0 && (
// //               <tr>
// //                 <td
// //                   colSpan={7}
// //                   className="px-4 py-4 text-center text-slate-500"
// //                 >
// //                   No parties yet.
// //                 </td>
// //               </tr>
// //             )}
// //             {!loading &&
// //               parties.map((p) => (
// //                 <tr key={p.id} className="border-t">
// //                   <td className="px-4 py-2">{p.name}</td>
// //                   <td className="px-4 py-2">{p.contact}</td>
// //                   <td className="px-4 py-2">{p.address}</td>
// //                   <td className="px-4 py-2 text-right">
// //                     ₹{(p.debit ?? 0).toFixed(2)}
// //                   </td>
// //                   <td className="px-4 py-2 text-right">
// //                     ₹{(p.credit ?? 0).toFixed(2)}
// //                   </td>
// //                   <td className="px-4 py-2 text-center">
// //                     <select
// //                       value={p.paymentStatus || "Pending"}
// //                       onChange={(e) =>
// //                         handleStatusChange(p, e.target.value)
// //                       }
// //                       className="border rounded px-2 py-1 text-xs"
// //                     >
// //                       <option value="Pending">Pending</option>
// //                       <option value="Paid">Paid ✅</option>
// //                     </select>
// //                   </td>
// //                   <td className="px-4 py-2 text-center">
// //                     <div className="flex justify-center gap-2">
// //                       <button
// //                         onClick={() => openEditModal(p)}
// //                         className="px-2 py-1 text-xs bg-slate-100 rounded hover:bg-slate-200"
// //                       >
// //                         Edit
// //                       </button>
// //                       <button
// //                         onClick={() => handleDelete(p.id)}
// //                         className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
// //                       >
// //                         Delete
// //                       </button>
// //                     </div>
// //                   </td>
// //                 </tr>
// //               ))}
// //           </tbody>
// //         </table>
// //       </div>

// //       {/* Modal */}
// //       {showModal && (
// //         <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
// //           <div className="bg-white rounded-lg shadow w-full max-w-lg">
// //             <div className="flex justify-between items-center px-6 py-4 border-b">
// //               <h3 className="text-lg font-semibold text-slate-800">
// //                 {editingParty ? "Edit Party" : "Add Party"}
// //               </h3>
// //               <button
// //                 onClick={() => setShowModal(false)}
// //                 className="text-slate-400 hover:text-slate-600"
// //               >
// //                 ✕
// //               </button>
// //             </div>

// //             <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
// //               <div>
// //                 <label className="block text-sm mb-1">Name</label>
// //                 <input
// //                   name="name"
// //                   value={form.name}
// //                   onChange={handleFormChange}
// //                   className="w-full border rounded px-3 py-2"
// //                   required
// //                 />
// //               </div>

// //               <div>
// //                 <label className="block text-sm mb-1">Contact</label>
// //                 <input
// //                   name="contact"
// //                   value={form.contact}
// //                   onChange={handleFormChange}
// //                   className="w-full border rounded px-3 py-2"
// //                 />
// //               </div>

// //               <div>
// //                 <label className="block text-sm mb-1">Address</label>
// //                 <textarea
// //                   name="address"
// //                   value={form.address}
// //                   onChange={handleFormChange}
// //                   className="w-full border rounded px-3 py-2"
// //                 />
// //               </div>

// //               <div className="grid grid-cols-2 gap-3">
// //                 <div>
// //                   <label className="block text-sm mb-1">
// //                     Debit (They owe us)
// //                   </label>
// //                   <input
// //                     type="number"
// //                     step="0.01"
// //                     name="debit"
// //                     value={form.debit}
// //                     onChange={handleFormChange}
// //                     className="w-full border rounded px-3 py-2"
// //                   />
// //                 </div>
// //                 <div>
// //                   <label className="block text-sm mb-1">
// //                     Credit (We owe them)
// //                   </label>
// //                   <input
// //                     type="number"
// //                     step="0.01"
// //                     name="credit"
// //                     value={form.credit}
// //                     onChange={handleFormChange}
// //                     className="w-full border rounded px-3 py-2"
// //                   />
// //                 </div>
// //               </div>

// //               <div>
// //                 <label className="block text-sm mb-1">
// //                   Payment Status
// //                 </label>
// //                 <select
// //                   name="paymentStatus"
// //                   value={form.paymentStatus}
// //                   onChange={handleFormChange}
// //                   className="w-full border rounded px-3 py-2"
// //                 >
// //                   <option value="Pending">Pending</option>
// //                   <option value="Paid">Paid ✅</option>
// //                 </select>
// //               </div>

// //               <div className="flex justify-end gap-3 pt-2">
// //                 <button
// //                   type="button"
// //                   onClick={() => setShowModal(false)}
// //                   className="px-4 py-2 border rounded"
// //                 >
// //                   Cancel
// //                 </button>
// //                 <button
// //                   type="submit"
// //                   className="px-4 py-2 bg-blue-600 text-white rounded"
// //                 >
// //                   {editingParty ? "Update" : "Add"}
// //                 </button>
// //               </div>
// //             </form>
// //           </div>
// //         </div>
// //       )}
// //     </div>
// //   );
// // }
